/**
 * StageInfoDialog — (?) modal for the Stage tab.
 *
 * Explains what the Stage panel shows (assumed vs digest-inferred stages,
 * the digest_authoritative sync, the trajectory chart) and lists every one
 * of the 13 connection stages with a canonical one-line description sourced
 * from `canon_silos/v1.0/models/13_stage_model.md`.
 */
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  HelpCircle,
  GitCompare,
  RefreshCw as SyncIcon,
  AlertTriangle,
  Sparkles,
} from "lucide-react";

const STAGES = [
  { n: 0, name: "NONE", desc: "No current connection or awareness exists between individuals." },
  { n: 1, name: "SPARK", desc: "Initial awareness and curiosity about someone new, with a hint of intrigue." },
  { n: 2, name: "OPENING", desc: "The first direct interaction occurs, marking the start of exploratory communication." },
  { n: 3, name: "RESONANCE", desc: "Conversation flows easily and naturally, indicating shared interests and enjoyment." },
  { n: 4, name: "SAFETY", desc: "Trust begins to form through consistency, reliability, and respectful interactions." },
  { n: 5, name: "CURIOSITY", desc: "Interest deepens, leading to questions about values, stories, and inner worlds." },
  { n: 6, name: "ATTRACTION", desc: "Multi-level chemistry intensifies, with growing desire for closeness." },
  { n: 7, name: "VULNERABILITY", desc: "Walls soften as honest feelings, fears, and authentic selves are shared." },
  { n: 8, name: "ALIGNMENT", desc: "Core values, intentions, and future visions match, creating a compatible direction." },
  { n: 9, name: "MOMENTUM", desc: "The connection actively strengthens through shared time, effort, and purposeful progression." },
  { n: 10, name: "INTIMACY", desc: "Deep emotional and/or physical closeness is achieved, with profound mutual understanding." },
  { n: 11, name: "INTEGRATION", desc: "Lives and daily routines begin to weave together, merging social circles and practical matters." },
  { n: 12, name: "COMMITMENT", desc: "A clear, conscious choice is made to invest in and prioritise the relationship long-term." },
  { n: 13, name: "UNION", desc: "A stable, mutual bond is established, characterised by trust, love, and shared direction." },
];

export default function StageInfoDialog() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="About the Stage panel & the 13 connection stages"
        aria-label="About the Stage panel"
        data-testid="stage-info-btn"
        className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full text-white/55 hover:text-divine-gold hover:bg-divine-gold/10 transition-colors focus:outline-none focus:ring-2 focus:ring-divine-gold/40"
      >
        <HelpCircle className="h-4 w-4" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-divine-charcoal border-divine-gold/25 text-white/85 max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="stage-info-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-cinzel text-divine-gold flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              About the Stage panel
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 text-sm leading-relaxed">
            <section>
              <h4 className="text-divine-gold/90 text-xs uppercase tracking-wide mb-1.5">
                What this panel represents
              </h4>
              <p>
                Every relationship is a journey across{" "}
                <span className="text-divine-gold">13 connection stages</span>,
                from <em>NONE</em> (no awareness) through <em>UNION</em>{" "}
                (stable mutual bond). Movement is rarely linear — pairs can
                progress, regress, plateau, or sit at different stages from
                each other. The Stage panel makes that journey visible.
              </p>
              <p className="mt-2 text-white/65">
                Three lenses are tracked at once: where{" "}
                <strong className="text-divine-gold">you assume</strong> the
                relationship sits, where{" "}
                <strong className="text-emerald-300">Eros reads you</strong>{" "}
                across recent sessions, and where{" "}
                <strong className="text-sky-300">Eros reads them</strong>.
                Gaps between those three are the most useful coaching
                signals in this app.
              </p>
            </section>

            <section>
              <h4 className="text-divine-gold/90 text-xs uppercase tracking-wide mb-2 flex items-center gap-2">
                <GitCompare className="h-3.5 w-3.5" />
                The Connection-stage bar at the top
              </h4>
              <p>
                The three (or two) coloured tiles at the top of this panel
                are the live <em>Connection stage</em> view:
              </p>
              <ul className="mt-2 space-y-2 text-xs">
                <li className="flex gap-2">
                  <span className="shrink-0 inline-flex items-center justify-center w-16 rounded border border-divine-gold/40 bg-divine-gold/10 text-divine-gold text-[10px] font-medium">
                    ASSUMED
                  </span>
                  <span className="text-white/75">
                    The stage value you set on the match profile — your
                    conscious read of where things stand. Editable any time
                    from the match editor.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="shrink-0 inline-flex items-center justify-center w-16 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-medium">
                    YOU
                  </span>
                  <span className="text-white/75">
                    Where Eros's coaching read places you based on{" "}
                    <em>how you describe the relationship</em> across recent
                    coaching sessions. This is the coaching read of your
                    psychology in the relationship, not yours of theirs.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="shrink-0 inline-flex items-center justify-center w-16 rounded border border-sky-500/40 bg-sky-500/10 text-sky-300 text-[10px] font-medium">
                    THEM
                  </span>
                  <span className="text-white/75">
                    Where Eros's coaching read places them based on{" "}
                    <em>your reports of their behaviour and statements</em>.
                    A second-order inference — only as good as the evidence
                    you bring into session.
                  </span>
                </li>
              </ul>
              <p className="mt-3 text-white/65 text-xs">
                When your <strong className="text-divine-gold">assumed</strong>{" "}
                stage disagrees with the coaching read of you, a one-line
                summary appears beneath the tiles describing the gap. That
                tension is the most honest mirror Eros can hold up — a good
                coach surfaces the disconnect, doesn't paper over it.
              </p>
            </section>

            <section>
              <h4 className="text-divine-gold/90 text-xs uppercase tracking-wide mb-2 flex items-center gap-2">
                <SyncIcon className="h-3.5 w-3.5" />
                The "Sync from Coaching" checkbox
              </h4>
              <p className="text-white/80">
                Off by default. When you tick it on, the coaching read becomes
                the authority: after each coaching session, Eros writes its read
                of your stage back into your match profile automatically
                (medium/high-confidence inferences only).
              </p>
              <ul className="mt-2 space-y-1 text-xs text-white/65 list-disc list-inside marker:text-divine-gold/50">
                <li>
                  <strong className="text-white/85">OFF:</strong> you stay
                  the authority — 3 tiles, gap summary visible whenever the
                  coaching read disagrees with your assumption.
                </li>
                <li>
                  <strong className="text-white/85">ON:</strong> your
                  assumed stage and the coaching read of you are kept in
                  lockstep — 2 tiles only (no duplication), no gap line.
                </li>
              </ul>
              <p className="mt-2 text-white/55 text-xs italic">
                Useful if you want Eros's read to lead. Equally useful kept
                off if you'd rather see your own blind spots — a stubborn
                assumption with a persistent coaching disconnect is itself
                worth bringing into a session.
              </p>
            </section>

            <section>
              <h4 className="text-divine-gold/90 text-xs uppercase tracking-wide mb-2 flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5" />
                The asynchronicity alert
              </h4>
              <p className="text-white/75 text-xs">
                Amber alert below the tiles. Fires when the latest coaching
                read detects a meaningful gap between where you sit and where
                they sit (the YOU vs THEM tiles, not assumed-vs-coaching).
                The accompanying reasoning is what Eros saw in the session
                that prompted the flag.
              </p>
            </section>

            <section>
              <h4 className="text-divine-gold/90 text-xs uppercase tracking-wide mb-2">
                The 13 connection stages
              </h4>
              <ol className="space-y-1.5" data-testid="stage-info-stage-list">
                {STAGES.map((s) => (
                  <li
                    key={s.name}
                    className="flex gap-3 text-xs leading-snug"
                    data-testid={`stage-info-row-${s.n}`}
                  >
                    <span className="shrink-0 inline-flex items-baseline gap-1 text-divine-gold/90 font-medium min-w-[110px]">
                      <span className="tabular-nums">{s.n}.</span>
                      <span>{s.name}</span>
                    </span>
                    <span className="text-white/70">{s.desc}</span>
                  </li>
                ))}
              </ol>
              <p className="mt-3 text-white/45 text-[11px] italic">
                Canonical descriptions sourced from Agent Eros's 13-stage
                model silo. Refer to your coach for nuance — the labels are
                deliberately compact and don't capture every dimension of a
                stage.
              </p>
            </section>

            <p className="text-white/40 text-[11px] pt-2 border-t border-divine-gold/10">
              The trajectory chart beneath the tiles plots every recorded
              stage change over time — progressions in emerald, regressions
              in rose, confirmations in muted grey. Tap any history row to
              read Eros's reasoning for that movement.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
